import {useEffect, useMemo, useRef, useState} from "react";

import {AgGridReact} from "ag-grid-react";
import Box from "@mui/material/Box"
import { SettingsButton } from "@plannel/components/ActionIconButton";

import classificationResultService from "@plannel/services/ip/classification-result-service";
import ExcelExportButton from "@plannel/components/ExcelExportButton"
import PaginationContainer from "@plannel/components/PaginationContainer"
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import GridUtils from "@plannel/components/aggrid/GridUtils";
import Snackbar from "@plannel/components/Snackbar";
import ClassificationSettingsModal from "@plannel/components/modal/ClassificationSettingsModal";
import {useDispatch} from "react-redux";
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import debug from "@plannel/utils/debug-util";
import {withTranslation} from "react-i18next";
import {cloneDeep, includes, isEmpty, isEqual, size, replace, isNull, isUndefined} from "lodash";
import ColorUtil from "@plannel/pages/utils/color-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import AbcXyzChip from "@plannel/components/aggrid/AbcXyzChip";
import NumberUtil from "@plannel/utils/number-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import ClassificationFilter from "@plannel/components/filter/ClassificationFilter";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import { IPLocationTreeFilter } from "@plannel/components/filter/LocationTreeFilter";
import { IPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";

const { versionStatusCode } = CodeUtil;
const { READY, PLANNED } = versionStatusCode;
const { snackBarMessageColor } = ColorUtil;
const SALES_VAL = "salesVal";
const ALL = "ALL";
const DC_ALL = "DC_ALL";
const TRANSLATION_VALUE = ["PLANT_ALL", DC_ALL, ALL, "NONE"];
const AbcXyzAnalysisResults = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();
  // 월별 재고 추이로 가는 드릴 — 권한 밖이면 링크 표시도 클릭도 없앤다.
  const canDrillMonthlyTrend = useMenuAccess().isPermitted("INV_MON_TREND");
  const cellStyle = canDrillMonthlyTrend ? { color: 'blue', textDecoration: 'underline', cursor: 'pointer' } : null;

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);
  const [sortParams, setSortParams] = useState({});
  
  const [totalPages, setTotalPages] = useState(0);
  const [abcAnalysisResults, setAbcAnalysisResults] = useState([]);

  const [classificationFilter, setClassificationFilter] = useState(reduxViewState?.classification ? reduxViewState.classification : null);
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { locations: null, category: null });
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);
  
  const userData = JSON.parse(localStorage.getItem("user"));
  const currencyMap = useRef({});
  const [csvColDef, setCsvColDef] = useState([]);
  const EXCEL_EXPORT = "excelExport";

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onItemCellClicked = (events) => {
    if (!canDrillMonthlyTrend) return;
    const fieldName = events.column.colDef.field;

    if (fieldName === "itemCd") {
      const { data } = events;
      const { categoryCd, categoryName, locCd, locName } = data;

      const location = {
        locCd: isEqual(locCd, "ALL") ? "" : locCd,
        name: isEqual(locCd, "ALL") ? "" : locName
      };

      const filter = {
        category: [{ hrchyCd: categoryCd, name: categoryName }], // Category 는 Multi Select 이므로 Array 로 설정
        locations: isEqual(locCd, "ALL") ? null : [location]
      };

      const itemCd = data?.itemCd;

      const reduxKey = "INV_MON_TREND";
      const viewState = reduxUtil.createObjectState(reduxKey, { filter, itemSearch: itemCd });
      reduxDispatch(reduxUtil.updateViewState(viewState));
      reduxDispatch(reduxUtil.updateHistoryState(reduxKey));
    }
  }

  const currencyRenderer = (param, isExport) => {
    const locCd = param?.data?.locCd;
    let symbol = userData?.currencySymbol;
    if (size(Object.keys(currencyMap?.current))) {
      const currencyCd = currencyMap.current[locCd];
      if (currencyCd) {
        symbol = CodeUtil.getCurrencySymbol(currencyCd);
      } 
    }
    const value = param?.data?.[param?.colDef?.field];
    if (isNull(value) || isUndefined(value)) {
      return "";
    }
    const formatValue = NumberUtil.formatNumber(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 });

    if (isExport) {
      return formatValue.replace(/,/g, "");
    }

    if (value < 0) {
      return `-${symbol}${replace(formatValue, "-", "")}`;
    }
    return symbol + formatValue;
  };
  currencyRenderer.customType = EXCEL_EXPORT;

  const locValueFormatter = (params) => {
    let value = params?.value;

    if (TRANSLATION_VALUE.includes(value)) {
      if (value === ALL) {
        value = DC_ALL;
      }

      return GridUtils.gridValueL10N(value);
    }

    return value;
  }

  const categoryValueFormatter = (params) => {
    const value = params?.value;

    if (TRANSLATION_VALUE.includes(value)) {
      return GridUtils.gridValueL10N(value);
    }

    return value;
  }
  
  const defaultColumnDefs = [
    { headerName: "id", field: "id", type: ["nonEditableColumn", "defaultValueFormatter"], width: 50, hide: true, suppressColumnsToolPanel: true },
    { headerName: "classifiId", field: "classifiId", type: ["nonEditableColumn"], width: 50, hide: true, suppressColumnsToolPanel: true },
    { headerName: "locCd", field: "locCd", type: ["nonEditableColumn"], valueFormatter: locValueFormatter, cellClass: 'stringType', width: 160 },
    { headerName: "locName", field: "locName", type: ["nonEditableColumn"], valueFormatter: locValueFormatter, width: 160 },
    { headerName: "categoryCd", field: "categoryCd", type: ["nonEditableColumn"], valueFormatter: categoryValueFormatter, cellClass: 'stringType', width: 130 },
    { headerName: "categoryName", field: "categoryName", type: ["nonEditableColumn"], valueFormatter: categoryValueFormatter, width: 150 },
    { headerName: "itemCd", field: "itemCd", type: ["nonEditableColumn"], onCellClicked: onItemCellClicked, cellStyle, cellClass: 'stringType', width: 140 },
    { headerName: "itemName", field: "itemName", type: ["nonEditableColumn"], width: 400 },
    { headerName: "invClassCd", field: "invClassCd", type: ["nonEditableColumn"], cellRenderer: AbcXyzChip, cellClass: 'stringType', width: 110 },
    { headerName: "salesAmt", field: SALES_VAL, colId: GridUtils.IGNORE_P13N_COLLID, type: ["numberColumn", "nonEditableColumn", "rightAligned"], width: 140},
    { headerName: "cumSum", field: "cumSum", type: [ "numberColumn", "nonEditableColumn", "rightAligned"], width: 140 },
    { headerName: "cumPct", field: "cumPct", type: ["detailPercentColumn", "nonEditableColumn", "rightAligned"], width: 130 }
  ];

  const getRequestParams = (isPagination = true) => {
    const params = {};

    if (classificationFilter) {
      params.classificationId = classificationFilter.id;
    }

    if (filterValue?.category) {
      params.categoryCd = filterValue.category.filter((category) => category?.leafFlg).map((category) => category?.hrchyCd);
    }

    if (filterValue?.locations) {
      params.locCd = filterValue?.locations?.map(location => location?.locCd);
    }

    if (extraItemCodesRef.current?.length > 0) {
      params.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      params.item = itemSearch.current;
    }

    if (isPagination) {
      if (currentPage) {
        params.page = currentPage - 1;
      }

      if (pageSize) {
        params.pageSize = pageSize;
      }
      if (sortParams?.orderByColumn === GridUtils.IGNORE_P13N_COLLID) {
        sortParams.orderByColumn = "salesVal"
      }
    }

    return { ...params, ...sortParams };
  };

  /*
   * headerName 에는 **번역 키만** 넣는다(번역은 GridUtils 가 한다).
   * 번역문을 넣어 두면 registerLocalizedGrid 가 보관하는 원본도 번역문이 되어
   * 언어를 바꿨을 때 키를 되찾지 못해 헤더가 옛 언어로 굳는다(GridUtils L208 주석).
   */
  const dynamicHeaderName = (base, plantLocAnalysis) => {
    const { classifiBaseCode } = CodeUtil;
    const { QUANTITY, REVENUE, AMOUNT, COGS } = classifiBaseCode;

    const newColumns = cloneDeep(defaultColumnDefs);
    const newCsvColumns = cloneDeep(defaultColumnDefs);
    const includeCols = [SALES_VAL, "cumSum"];

    const prefixColName = plantLocAnalysis ? "shipment" : "sales";
    if (base === QUANTITY) {
      newColumns.forEach((column) => {
        if (includes(includeCols, column.field)) {
          if (column.field === SALES_VAL){
            column.headerName = `${prefixColName}Qty`;
          }
        }
      });
      newCsvColumns.forEach((column) => {
        if (includes(includeCols, column.field)) {
          if (column.field === SALES_VAL){
            column.headerName = `${prefixColName}Qty`;
          }
        }
      });
    } else if(base === REVENUE || base === AMOUNT) {
      newColumns.forEach((column) => {
        if (includes(includeCols, column.field)) {
          if (column.field === SALES_VAL) {
            column.headerName = `${prefixColName}Amt`;
          };
          column.valueFormatter = currencyRenderer
        }
      });
      newCsvColumns.forEach((column) => {
        if (includes(includeCols, column.field)) {
          if (column.field === SALES_VAL) {
            column.headerName = `${prefixColName}Amt`;
          };
        }
      });
    } else if (base === COGS) {
      newColumns.forEach((column) => {
        if (includes(includeCols, column.field)) {
          if (column.field === SALES_VAL) {
            column.headerName = "cogs";
          };
          column.valueFormatter = currencyRenderer
        }
      });
      newCsvColumns.forEach((column) => {
        if (includes(includeCols, column.field)) {
          if (column.field === SALES_VAL) {
            column.headerName = "cogs";
          };
        }
      });
    } else {
      newColumns.forEach((column) => {
        if (includes(includeCols, column.field)) {
          if (column.field === SALES_VAL) {
            column.headerName = "profit";
          };
          column.valueFormatter = currencyRenderer
        }
      });
      newCsvColumns.forEach((column) => {
        if (includes(includeCols, column.field)) {
          if (column.field === SALES_VAL) {
            column.headerName = "profit";
          };
        }
      });
    }
    GridUtils.setColumnDefs({ ...gridRef.current, columnDefs: newColumns, viewName });
    // 엑셀의 translationKey 시트는 headerName 을 그대로 읽으므로 번역해서 넘긴다.
    setCsvColDef(GridUtils.getColumnDefs(newCsvColumns));
  }

  const retrieveAbcAnalysisResults = () => {
    const params = getRequestParams();

    classificationResultService.getAll(params)
      .then(response => {
        const { base, plantLocAnalysis, totalPages, results, currency } = response.data;
        if (results && results.length > 0) {
          dynamicHeaderName(base, plantLocAnalysis);
          setAbcAnalysisResults(results);
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
          setAbcAnalysisResults([]);
        }

        setTotalPages(totalPages);
        
        if (size(currency)) {
          currencyMap.current = currency;
        }
        
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const pageState = reduxUtil.createObjectState(viewName, { itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, currentPage, pageSize });
        const filterState = reduxUtil.createFilterViewState(viewName, filterValue);
        const reduxState = {...pageState, ...filterState};
        reduxDispatch(reduxUtil.updateViewState(reduxState));
      })
  };

  const onGridReady = params => {
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs: defaultColumnDefs, viewName, initState: true });
  };

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveAbcAnalysisResults();
    }
  }

  useEffect(() => {
    if (gridLoading && !isEmpty(classificationFilter)) {
      retrieveAbcAnalysisResults();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (gridLoading && !isEmpty(classificationFilter)) {
      initPageToRetrieve();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classificationFilter?.id, filterValue]);

  return (
    <>
      <FilterContainer>
        <ClassificationFilter viewName={viewName} classificationValue={classificationFilter} onChangeFilter={setClassificationFilter} statusFilters={[READY, PLANNED]} />
        <ItemHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} enableMultiSelect selectLeafs styles={{ width: 240 }} />
        <IPLocationTreeFilter viewName={viewName} filterValue={filterValue?.locations?.map(location => location?.locCd)} onChangeFilter={setFilterValue} addLocType={[CodeUtil.locationType.PLANT.cd]}/>
        <IPItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} extraCodesRef={extraItemCodesRef}/>
        <Box sx={{ ml: "auto" }}>
          <SettingsButton
            disabled={isEmpty(classificationFilter)}
            onClick={() => setSettingsModalOpen(true)}
          />
          <ExcelExportButton title={title} service={classificationResultService} columnDefs={csvColDef?.filter(column => column.hide !== true)} params={getRequestParams(false)} />
        </Box>
      </FilterContainer>
      <ClassificationSettingsModal
        open={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
        classification={classificationFilter}
      />
      <Box className="ag-theme-balham grid-area" mb={1}>
        <AgGridReact
          ref={gridRef}
          rowData={abcAnalysisResults}
          {...defaultGridMemo}
          onGridReady={onGridReady}
          onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
        />
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={totalPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(AbcXyzAnalysisResults);
